// BCG 분석 메인 함수

import {
  MentalHealthReport,
  AnalysisParams,
  MindScoreResult
} from './types';
import { reconstructSeries, windowize } from './signalProcessing';
import {
  mindScoreRelative,
  mindScoreAbsolute,
  getDecisionLabel,
  calculateStressScore,
  analyzeBalance,
  analyzeRespirationState,
  buildCalibrator
} from './scoring';
import { calculateHRVPSD } from './psdAnalysis';
import { predictAllSymptoms } from './symptomPrediction';

/**
 * BCG 데이터 전체 분석
 */
export function analyzeBCGData(
  csvData: any[],
  params: AnalysisParams
): MentalHealthReport | null {
  // console.log(`BCG 분석 시작 - 데이터 행 수: ${csvData.length}, 파라미터:`, params);

  // 1. 신호 재구성
  const reconstructed = reconstructSeries(csvData, 256);
  // console.log(`신호 재구성 결과:`, reconstructed ? `성공 (길이: ${reconstructed.data.bcg.length})` : '실패');

  if (!reconstructed) {
    console.error('신호 재구성 실패 - 분석할 수 있는 데이터가 부족합니다.');
    return null;
  }

  const { data: sigData, fs } = reconstructed;

  // 2. 윈도우 분석
  const { windows, peaks } = windowize(sigData, fs, params.windowSec, params.strideSec);

  // 3. 마음건강 점수 계산
  let mindScore: MindScoreResult;
  if (params.mode === 'absolute') {
    mindScore = mindScoreAbsolute(windows);
  } else {
    mindScore = { score: mindScoreRelative(windows) };
  }

  // 4. PSD 분석
  const psdData = calculateHRVPSD(sigData, fs, peaks);

  // 5. 스트레스 분석
  const stressAnalysis = calculateStressScore(windows, psdData);

  // 6. 자율신경 균형 분석
  const balanceAnalysis = analyzeBalance(psdData.LFnu, psdData.HFnu);

  // 7. 호흡 상태 분석
  const validWindows = windows.filter(w => w.DQI >= 0.6 && w.rr_count >= 3);
  const respirationBpm = validWindows.length > 0
    ? validWindows.reduce((sum, w) => sum + (isFinite(w.Resp_bpm) ? w.Resp_bpm : 0), 0) / validWindows.length
    : NaN;
  const respirationState = analyzeRespirationState(respirationBpm);

  // 8. 증상 예측 분석
  const symptomPredictions = predictAllSymptoms(windows, psdData);
  // console.log('증상 예측 완료');

  // 9. 캘리브레이션 적용
  const calibrate = buildCalibrator(params.calibrationPairs || []);
  const calibratedScore = calibrate(mindScore.score);

  // 10. 의사결정 라벨 (절대 점수 기준)
  const decisionLabel = getDecisionLabel(params.mode === 'absolute' ? mindScore.score : calibratedScore);

  // 11. 품질 지표
  const duration_min = sigData.bcg.length / fs / 60.0;
  const validWindowRatio = validWindows.length / windows.length;

  return {
    mindScore: {
      ...mindScore,
      score: calibratedScore
    },
    stressAnalysis,
    balanceAnalysis,
    psdData,
    respirationState,
    decisionLabel,
    qualityMetrics: {
      analyzability: validWindowRatio,
      coverage: validWindowRatio,
      duration_min
    },
    symptomPredictions
  };
}

/**
 * 세션 요약 생성
 */
export function generateSessionSummary(reports: Array<{ name: string; report: MentalHealthReport }>): any[] {
  return reports.map(({ name, report }) => ({
    file: name,
    MindScore_abs: Math.round(report.mindScore.score * 100) / 100,
    MindScore_rel: Math.round((report.mindScore.score || 0) * 100) / 100,
    Label_abs: report.decisionLabel,
    Stress_category: report.stressAnalysis.category,
    LF_HF: Math.round((report.psdData.LF_HF || 0) * 100) / 100,
    Duration_min: Math.round(report.qualityMetrics.duration_min * 10) / 10,
    Quality: Math.round(report.qualityMetrics.analyzability * 100)
  })).sort((a, b) => b.MindScore_abs - a.MindScore_abs);
}

// 타입 및 함수 재내보내기
export * from './types';
export * from './signalProcessing';
export * from './scoring';
export * from './psdAnalysis';
